import { UserManager, WebStorageStateStore, type UserManagerSettings } from 'oidc-client-ts'

/**
 * ADR-0048 (Phase A, #7): real Authorization-Code+PKCE OIDC login against the demo's own Keycloak
 * realm (`deploy/demo/keycloak/incentiwise-realm.json`). Config is baked in at BUILD time via
 * `VITE_OIDC_*` env vars (see `deploy/demo/docker/frontend.Dockerfile` — Vite inlines
 * `import.meta.env.VITE_*` at build, there is no runtime env injection for a static SPA bundle).
 *
 * <p><b>Static `metadata` (no OIDC discovery fetch).</b> Keycloak's endpoint shapes are standard and
 * stable (`/protocol/openid-connect/{auth,token,userinfo,logout,certs}` under the realm base URL) —
 * providing them directly avoids an eager `.well-known/openid-configuration` network call the first
 * time `signinRedirect`/`signinSilent` runs, and keeps this module side-effect-free to import (no
 * network I/O happens merely by constructing a `UserManager`) — important for unit tests that render
 * components inside an `AuthProvider` without a live IdP reachable at all (e.g. `NavShell.test.tsx`).
 *
 * <p><b>`automaticSilentRenew: true`</b> — the design's "silent refresh via refresh token" requirement:
 * Keycloak's standard Authorization Code exchange already issues a refresh token (no `offline_access`
 * scope needed for a normal, session-bound one), and `oidc-client-ts` automatically renews via a
 * direct token-endpoint call using that refresh token when one is present on the current user,
 * rather than the iframe-based silent-renew flow (which needs a dedicated `silent_redirect_uri` page
 * this demo does not ship).
 */
function readEnv(key: string, fallback: string): string {
  const value = (import.meta.env as Record<string, string | undefined>)[key]
  return value && value.length > 0 ? value : fallback
}

const authority = readEnv('VITE_OIDC_AUTHORITY', 'http://localhost/auth/realms/incentiwise-demo')
const clientId = readEnv('VITE_OIDC_CLIENT_ID', 'incentiwise-spa')
const redirectUri = readEnv(
  'VITE_OIDC_REDIRECT_URI',
  typeof window !== 'undefined' ? window.location.origin + '/' : '/',
)

export function buildOidcUserManagerSettings(): UserManagerSettings {
  return {
    authority,
    client_id: clientId,
    redirect_uri: redirectUri,
    post_logout_redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid',
    automaticSilentRenew: true,
    // S3 fix (Verifier/security-reviewer, Phase A #7 v2) -- ACCEPTED DEMO RISK, recorded in
    // HANDOFF.md. The design doc (§3.5) called for holding the token in memory only; this ships
    // sessionStorage instead as the lower-risk resolution for THIS demo -- moving to a genuinely
    // in-memory store risks the login flow (oidc-client-ts's own redirect-callback round trip
    // relies on persisted state across the full-page navigation to Keycloak and back, which an
    // in-memory-only store loses). sessionStorage is tab-scoped (cleared on tab close, never
    // shared across tabs/origins) and this demo carries only synthetic data (CLAUDE.md invariant
    // #4) -- acceptable for a time-boxed stakeholder demo, NOT a pattern to carry into a real
    // deployment without re-visiting this decision.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    metadata: {
      issuer: authority,
      authorization_endpoint: `${authority}/protocol/openid-connect/auth`,
      token_endpoint: `${authority}/protocol/openid-connect/token`,
      userinfo_endpoint: `${authority}/protocol/openid-connect/userinfo`,
      end_session_endpoint: `${authority}/protocol/openid-connect/logout`,
      jwks_uri: `${authority}/protocol/openid-connect/certs`,
    },
  }
}

/** The one shared `UserManager` — passed to `react-oidc-context`'s `<AuthProvider>` AND imported
 * directly by `api/client.ts` (outside any React context) to read the current access token. */
export const oidcUserManager = new UserManager(buildOidcUserManagerSettings())
